import { useEffect, useState } from 'react'
import { api, type ProfileResult } from '../api/client'
import { useSettings } from '../store/settings'
import { blobUrl } from '../utils/handle'
import { ImageLightbox, type LightboxImage } from './ImageLightbox'

function blobLink(b: unknown): string | null {
  if (!b) return null
  if (typeof b === 'string') return b
  const o = b as { ref?: unknown }
  if (typeof o.ref === 'string') return o.ref
  if (o.ref && typeof o.ref === 'object') {
    const r = o.ref as { $link?: string }
    if (typeof r.$link === 'string') return r.$link
  }
  return null
}

export function ProfileBanner({ handle }: { handle: string }) {
  const service = useSettings((s) => s.service)
  const [profile, setProfile] = useState<ProfileResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [lightbox, setLightbox] = useState<LightboxImage | null>(null)

  useEffect(() => {
    let cancelled = false
    setProfile(null)
    setError(null)
    api
      .profile(handle, service)
      .then((p) => {
        if (!cancelled) setProfile(p)
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      })
    return () => {
      cancelled = true
    }
  }, [handle, service])

  if (error) {
    return (
      <div className="mx-auto mt-4 max-w-6xl px-4">
        <div className="glass border-rose-400/30 bg-rose-500/10 px-4 py-3 text-rose-100">
          Couldn't load profile for @{handle}: <span className="font-mono text-xs">{error}</span>
        </div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="mx-auto mt-4 max-w-6xl px-4">
        <div className="glass animate-pulse px-4 py-6 text-ink-300">Loading @{handle}…</div>
      </div>
    )
  }

  const p = profile
  const avatarLink = blobLink(p.profile?.avatar)
  const bannerLink = blobLink(p.profile?.banner)
  const avatarUrl = avatarLink ? blobUrl(p.did, avatarLink, 'avatar') : undefined
  const bannerUrl = bannerLink ? blobUrl(p.did, bannerLink, 'banner') : undefined
  function openAvatar() {
    if (!avatarLink) return
    setLightbox({
      src: blobUrl(p.did, avatarLink, 'avatar'),
      alt: `@${p.handle} avatar`,
      downloadName: `${p.handle}-avatar.jpg`,
      postUrl: `https://bsky.app/profile/${p.handle}`,
    })
  }
  function openBanner() {
    if (!bannerLink) return
    setLightbox({
      src: blobUrl(p.did, bannerLink, 'banner'),
      alt: `@${p.handle} banner`,
      downloadName: `${p.handle}-banner.jpg`,
      postUrl: `https://bsky.app/profile/${p.handle}`,
    })
  }

  return (
    <section className="mx-auto mt-4 max-w-6xl px-4">
      {/* The bio bubble lives in the TopBar header now. This section is just
          the banner cover art so the user can still see + open it. */}
      <div className="relative overflow-hidden rounded-2xl border border-white/10">
        {bannerUrl ? (
          <button
            type="button"
            onClick={openBanner}
            className="block w-full"
            title="open banner full-size"
          >
            <img src={bannerUrl} alt="" className="h-32 w-full object-cover sm:h-40" />
          </button>
        ) : (
          <div className="h-20 w-full bg-gradient-to-r from-sky-500/30 via-violet-500/20 to-rose-500/20" />
        )}
        {avatarUrl ? (
          <button
            type="button"
            onClick={openAvatar}
            className="absolute bottom-2 left-2 transition hover:scale-105"
            title="open avatar full-size"
          >
            <img
              src={avatarUrl}
              alt=""
              className="h-14 w-14 rounded-xl border-2 border-white/30 object-cover shadow-glass"
            />
          </button>
        ) : null}
      </div>
      {lightbox ? <ImageLightbox image={lightbox} onClose={() => setLightbox(null)} /> : null}
    </section>
  )
}
